import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, API, getFormat } from "@/lib/api";
import { Loader2, Send, Plus, Trash2, Sparkles, Bot, User, ArrowRightLeft } from "lucide-react";

const SUGGESTIONS = [
  "Should I trade Bijan Robinson for Ja'Marr Chase?",
  "Is it smart to sell Christian McCaffrey for two 2026 firsts?",
  "I have an aging contender. Which of my RBs should I move?",
  "Trade Jahmyr Gibbs + a 2nd for CeeDee Lamb — fair?",
];

export default function TradeAdvisor() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const tradeContextRef = useRef(null);
  const preloadRef = useRef(null); // {a:[ids], b:[ids]}
  const scrollRef = useRef(null);
  const prefillHandled = useRef(false);

  const loadSessions = useCallback(() => api.get("/advisor/sessions").then(r => setSessions(r.data.sessions || [])), []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (prefillHandled.current) return;
    const st = location.state;
    if (st?.tradeContext) {
      prefillHandled.current = true;
      tradeContextRef.current = st.tradeContext;
      preloadRef.current = { a: st.aIds || [], b: st.bIds || [] };
      sendMessage(st.prefill || "Should I make this trade?");
    }
  }, [location.state]); // eslint-disable-line

  const newChat = () => {
    setActiveId(null); setMessages([]); setInput("");
    tradeContextRef.current = null; preloadRef.current = null; prefillHandled.current = true;
  };

  const openSession = async (id) => {
    setActiveId(id);
    const r = await api.get(`/advisor/sessions/${id}`);
    setMessages(r.data.messages || []);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/advisor/sessions/${id}`);
    if (id === activeId) newChat();
    loadSessions();
  };

  const openInCalculator = () => {
    const pl = preloadRef.current;
    navigate("/app/trade-calculator", { state: pl ? { preloadA: pl.a, preloadB: pl.b } : {} });
  };

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    const body = { message: text };
    if (activeId) body.session_id = activeId;
    if (tradeContextRef.current) { body.trade_context = tradeContextRef.current; tradeContextRef.current = null; }
    try {
      const res = await fetch(`${API}/advisor/chat/stream?format=${getFormat()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      // Coalesce token deltas into ~60fps renders instead of one render per token.
      let acc = "";
      let scheduled = false;
      const flush = () => {
        scheduled = false;
        setMessages(prev => { const c = [...prev]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      };
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let ev; try { ev = JSON.parse(line); } catch { continue; }
          if (ev.type === "session") setActiveId(ev.session_id);
          else if (ev.type === "delta") {
            acc += ev.content;
            if (!scheduled) { scheduled = true; requestAnimationFrame(flush); }
          }
          else if (ev.type === "error") { acc = "⚠️ " + ev.content; flush(); }
          else if (ev.type === "done") { flush(); loadSessions(); }
        }
      }
      flush();
    } catch (e) {
      setMessages(prev => { const c = [...prev]; c[c.length - 1] = { role: "assistant", content: "⚠️ Connection error. Please try again." }; return c; });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fade-up h-[calc(100vh-7rem)] flex gap-4" data-testid="advisor-page">
      <div className="w-60 shrink-0 hidden lg:flex flex-col card-bordered overflow-hidden">
        <button onClick={newChat} className="btn-primary text-sm m-3 inline-flex items-center justify-center gap-2" data-testid="advisor-new-chat">
          <Plus className="w-4 h-4" /> New chat
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {sessions.map(s => (
            <div key={s.session_id} onClick={() => openSession(s.session_id)}
              className={`group flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-sm ${activeId === s.session_id ? "bg-[var(--accent)]/15 text-white" : "text-[var(--text-2)] hover:bg-white/5"}`}
              data-testid={`advisor-session-${s.session_id}`}>
              <span className="truncate flex-1">{s.title || "Untitled"}</span>
              <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-[var(--text-3)] hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {sessions.length === 0 && <div className="text-xs text-[var(--text-3)] px-2 py-3">No conversations yet.</div>}
        </div>
      </div>

      <div className="flex-1 min-w-0 flex flex-col card-bordered overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[var(--accent)]" />
          <div>
            <div className="font-display font-semibold leading-none">AI Trade Advisor</div>
            <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-1">CLAUDE · GROUNDED IN LIVE VALUES</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="advisor-messages">
          {messages.length === 0 && !sending && (
            <div className="max-w-lg mx-auto text-center mt-10">
              <div className="w-12 h-12 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-6 h-6 text-[var(--accent)]" />
              </div>
              <h2 className="font-display text-2xl font-bold">Ask about any trade</h2>
              <p className="text-[var(--text-2)] text-sm mt-2">I read live Dynasty Metrics valuations, market trends, and the news feed to tell you Accept, Decline, or Counter.</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-6 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => sendMessage(s)} className="card-bordered p-3 text-sm text-[var(--text-2)] hover:text-white hover:border-[var(--accent)]/40 transition-colors" data-testid={`advisor-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key -- chat is append-only; a stable index key is required so streaming updates the last bubble in place rather than remounting it
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-msg-${m.role}-${i}`}>
              {m.role === "assistant" && <div className="w-7 h-7 rounded-md bg-[var(--accent)]/15 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[var(--accent)]" /></div>}
              <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[var(--accent)] text-black" : "bg-[var(--elev)] border border-[var(--border)]"}`}>
                {m.role === "assistant"
                  ? (m.content
                      ? <>
                          {/* While the last message is still streaming, render plain text —
                              re-parsing markdown on every token makes long replies stutter.
                              Switch to full markdown once the stream completes. */}
                          {sending && i === messages.length - 1
                            ? <div className="md-body whitespace-pre-wrap leading-relaxed">{m.content}</div>
                            : <div className="md-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>}
                          {!sending && i === messages.length - 1 && (
                            <button onClick={openInCalculator} className="btn-secondary text-xs mt-3 inline-flex items-center gap-1.5" data-testid="advisor-open-calculator">
                              <ArrowRightLeft className="w-3.5 h-3.5" /> Open in Calculator
                            </button>
                          )}
                        </>
                      : <span className="inline-flex items-center gap-2 text-[var(--text-2)]"><Loader2 className="w-4 h-4 spin text-[var(--accent)]" /> Analyzing values & news…</span>)
                  : <span className="whitespace-pre-wrap">{m.content}</span>}
              </div>
              {m.role === "user" && <div className="w-7 h-7 rounded-md bg-[var(--elev)] border border-[var(--border)] flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>}
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--border)] p-3">
          <div className="flex items-end gap-2">
            <textarea
              className="input-dark resize-none flex-1 max-h-32" rows={1}
              placeholder="Describe the trade you're considering…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              data-testid="advisor-input"
            />
            <button onClick={() => sendMessage()} disabled={sending || !input.trim()} className="btn-primary inline-flex items-center gap-2 h-10" data-testid="advisor-send">
              {sending ? <Loader2 className="w-4 h-4 spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
